"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "../../button";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import { Alert, AlertDescription } from "../../alert";
import { Calendar, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { generateMicrosoftAuthUrl } from "../../../lib/oauth-utils";
import { useToast } from "../../../hooks/use-toast";

interface MicrosoftOAuthSetupProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  redirectUri?: string;
}

export const MicrosoftOAuthSetup = ({
  onSuccess,
  onError,
  redirectUri,
}: MicrosoftOAuthSetupProps) => {
  const [authUrl, setAuthUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const clientId = "fb359b19-637d-44ef-b98a-a55b2bd2b8d1";
  // Use the shared intermediate page so basePath rules are respected
  const defaultRedirectUri = `${window.location.origin}/unifiedtime/intermediate/`;
  const finalRedirectUri = redirectUri || defaultRedirectUri;

  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    onError?.(errorMessage);
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
  }, [onError, toast]);

  useEffect(() => {
    const generateAuthUrl = async () => {
      try {
        setIsLoading(true);

        const generatedAuthUrl = await generateMicrosoftAuthUrl(
          false,
          clientId,
          finalRedirectUri,
        );
        setAuthUrl(generatedAuthUrl);
      } catch (err) {
        const errorMessage = "Failed to generate authentication URL";
        handleError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    generateAuthUrl();
  }, [clientId, finalRedirectUri, handleError]);

  const handleAuth = () => {
    if (authUrl) {
      window.location.href = authUrl;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span>Preparing authentication...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Calendar className="h-6 w-6 text-blue-600" />
            <CardTitle>Microsoft Calendar Setup</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            Connect your Microsoft Calendar to view and manage your events in
            UnifiedTime.
          </p>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button 
            onClick={handleAuth} 
            disabled={!authUrl} 
            className="w-full bg-orange-600 hover:bg-orange-700 text-white border-2 border-orange-600 hover:border-orange-700 font-semibold py-3 text-base"
          >
            <CheckCircle className="mr-2 h-5 w-5" />
            Connect Microsoft Account
          </Button>

          <div className="text-sm text-muted-foreground space-y-1">
            <p>• Connect your Microsoft 365 or Outlook.com account</p>
            <p>• Access your calendar events and availability</p>
            <p>• Manage calendar permissions and settings</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
